import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {IEmploymentInfo} from '../../../../../models/employment-info';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {nonWhitespace, validateOptionalRequired} from '../../../../../services/validators';
import {EmploymentStatusData} from '../../../../../data-repositories/employment-status-data';
import {StatusPair} from '../../../../../models/status-pair';
import {NatureOfBusinessData} from '../../../../../data-repositories/nature-of-business-data';
import {NatureOfBusiness} from '../../../../../models/nature-of-business';
import {Country} from '../../../../../models/country';
import {CountryService} from '../../../../../data-repositories/country-service';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {ActivatedRoute} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-employment',
  templateUrl: './employment.component.html',
  styleUrls: ['./employment.component.scss']
})
export class EmploymentComponent implements OnInit, OnDestroy {
  private _employmentModel: IEmploymentInfo;
  private subscription$: Subscription;
  private destroy$ = new Subject<void>();

  employmentForm: FormGroup;
  employmentStatusList: Array<StatusPair>;
  natureOfBusinessList: Array<NatureOfBusiness>;
  countryList: Array<Country>;

  @Input()
  set employmentModel(value: IEmploymentInfo) {
    this._employmentModel = value;
    this.patchFormFromModel();
  }

  get employmentModel(): IEmploymentInfo {
    return this._employmentModel;
  }

  @Output() employmentModelChange = new EventEmitter<IEmploymentInfo>();

  @ViewChild('employmentFormElement') employmentFormElement: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    private focusInvalidService: FocusInvalidService,
    private route: ActivatedRoute,
    countryService: CountryService,
    employmentStatusData: EmploymentStatusData,
    natureOfBusinessData: NatureOfBusinessData,
  ) {
    this.buildForm();

    countryService.getCountryList().pipe(takeUntil(this.destroy$)).subscribe(cl => {
      this.countryList = cl;
    });

    this.employmentStatusList = employmentStatusData.getEmploymentStatusList();
    this.natureOfBusinessList = natureOfBusinessData.getNatureOfBusinessList();
  }

  ngOnInit() {
    this.patchFormFromModel();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params.validate) {
        this.validationService.markAllFieldsAsTouched(this.employmentForm);
        this.focusInvalidService.selectInvalidInput(this.employmentFormElement);
      }
    });

    this.subscription$ = this.validationService.onUserUpdate.subscribe(() => {
      this.validationService.markAllFieldsAsTouched(this.employmentForm);

      this.focusInvalidService.selectInvalidInput(this.employmentFormElement);
      this.validationService.validate(this.employmentForm.valid, 'EmploymentComponent');
    });

    this.subscribeAllInputsOnUpdate();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  updateUser(): void {
    this.employmentModelChange.emit(this.employmentModel);
  }

  isRequiredField(fieldName: string): boolean {
    return this.employmentForm.controls[fieldName].invalid
      && (this.employmentForm.controls[fieldName].dirty || this.employmentForm.controls[fieldName].touched)
      && (this.employmentForm.controls[fieldName].errors.required || this.employmentForm.controls[fieldName].errors.pattern);
  }

  isEmployed(): boolean {
    return this.employmentModel &&
      (this.employmentModel.employmentStatus === 'EMPLOYED' ||
        this.employmentModel.employmentStatus === 'SELFEMPLOYED');
  }

  isSelfEmployed(): boolean {
    return this.employmentModel && this.employmentModel.employmentStatus === 'SELFEMPLOYED';
  }

  filterCountryList(countryList: Array<Country>, filter: string, includeUSA: boolean = true) {
    if (!countryList) {
      return new Array<Country>();
    }

    if (filter && filter.trim().length > 0) {
      return countryList.filter(x => x.countryName !== 'United States' || includeUSA)
        .filter(x => x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase()));
    }

    return countryList.filter(x => x.countryName !== 'United States' || includeUSA);
  }

  getCountryCode(citizenship: string): string {
    if (citizenship && this.countryList && this.countryList.some(x => x.countryName === citizenship)) {
      return this.countryList.find(x => x.countryName === citizenship).countryCode;
    }

    return '';
  }

  getTranslate(country: string) {
    if (!country || !this.countryList || !this.countryList.some(x => x.countryName === country)) {
      return '';
    }

    return this.countryList.find(x => x.countryName === country).countryTranslation;
  }

  getOccupationList() {
    if (!this.employmentModel.natureOfBussiness) {
      return this.natureOfBusinessList[0].businessList;
    } else {
      return this.natureOfBusinessList.find(nof => nof.natureName === this.employmentModel.natureOfBussiness).businessList;
    }
  }

  private buildForm() {
    this.employmentForm = this.formBuilder.group({
      employmentStatus: new FormControl('', [Validators.required, nonWhitespace()]),
      employer: new FormControl('', [validateOptionalRequired(() => this.isEmployed()), nonWhitespace()]),
      natureOfBusiness: new FormControl('', [validateOptionalRequired(() => this.isEmployed())]),
      occupation: new FormControl('', [validateOptionalRequired(() => this.isEmployed() && this.employmentModel.natureOfBussiness !== 'Other')]),
      occupationOther: new FormControl('', [validateOptionalRequired(() => this.isEmployed() && this.employmentModel.natureOfBussiness === 'Other'), nonWhitespace()]),
      businessDescription: new FormControl('', [validateOptionalRequired(() => this.isSelfEmployed()), nonWhitespace()]),
    });
  }

  private patchFormFromModel(): void {
    if (!this._employmentModel || !this.employmentForm) {
      return;
    }

    this.employmentForm.patchValue({
      employmentStatus: this._employmentModel.employmentStatus ?? '',
      employer: this._employmentModel.employer ?? '',
      natureOfBusiness: this._employmentModel.natureOfBussiness ?? '',
      occupation: this._employmentModel.occupation ?? '',
      occupationOther: this._employmentModel.occupation ?? '',
      businessDescription: this._employmentModel.businessDescription ?? '',
    }, {emitEvent: false});
    formLog.state('Employment', this._employmentModel, this.employmentForm);
  }

  private syncModelFromForm(): void {
    if (!this._employmentModel) {
      return;
    }

    const value = this.employmentForm.getRawValue();
    this._employmentModel.employmentStatus = value.employmentStatus;
    this._employmentModel.employer = value.employer;
    this._employmentModel.natureOfBussiness = value.natureOfBusiness;
    this._employmentModel.businessDescription = value.businessDescription;

    if (this._employmentModel.natureOfBussiness === 'Other') {
      this._employmentModel.occupation = value.occupationOther;
    } else {
      this._employmentModel.occupation = value.occupation;
    }
    formLog.state('Employment', this._employmentModel, this.employmentForm);
  }

  private subscribeAllInputsOnUpdate() {
    this.employmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.syncModelFromForm();
      this.updateUser();
    });
  }

  onNatureOfBussinessChanged() {
    if (this.employmentModel.natureOfBussiness === 'Other') {
      this.employmentModel.occupation = '';
    }
  }
}
